import { reportNumber } from './rivalReport';

export const playerStatisticValue = (player, section, field) => {
  const value = Number(player?.[section]?.[field] ?? player?.[field] ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const recordedStatistic = (record, section, field) =>
  reportNumber(record?.[section]?.[field] ?? record?.[field]);

const recordedVenueTotal = (record, section, field, scope) => {
  const venue = suffix => {
    const key = `${field}${suffix}`;
    const raw = record?.[section]?.[key] !== undefined ? record[section][key] : record?.[key];
    if (raw !== undefined) return reportNumber(raw);
    // The basic writer stores only increments: a tracked call-up block has implicit zero counters.
    const tracked = ['Local', 'Visitante'].some(side =>
      recordedStatistic(record, 'partidos', `convocatorias${side}`) !== null);
    if (tracked && section === 'partidos') return 0;
    if (tracked && section === 'minutos' && recordedVenueTotal(record, 'partidos', 'partidosJugados',
      suffix === 'Local' ? 'local' : 'visitante') === 0) return 0;
    return null;
  };
  if (scope !== 'total') return venue(scope === 'local' ? 'Local' : 'Visitante');
  const home = venue('Local'), away = venue('Visitante');
  return home === null || away === null ? null : home + away;
};

const recordedShare = (numerator, denominator) => numerator !== null && denominator > 0
  && numerator <= denominator ? 100 * numerator / denominator : null;

export const playerTeamStatisticsId = player =>
  [player?.idEquipo, player?.idCompeticion, player?.idGrupo, player?.idTemporada]
    .every(value => value !== null && value !== undefined && String(value) !== '')
    ? `${player.idEquipo}_${player.idCompeticion}_${player.idGrupo}_${player.idTemporada}` : null;

export function playerParticipationRates(player, team, competition, scope = 'total') {
  const played = recordedVenueTotal(player, 'partidos', 'partidosJugados', scope);
  const called = recordedVenueTotal(player, 'partidos', 'convocatorias', scope);
  const starts = recordedVenueTotal(player, 'partidos', 'titularidades', scope);
  const minutes = recordedVenueTotal(player, 'minutos', 'minutosJugados', scope);
  const sameTeam = playerTeamStatisticsId(player) !== null && team
    && ['idEquipo', 'idCompeticion', 'idGrupo', 'idTemporada'].every(key =>
      team[key] != null && String(team[key]) === String(player[key]));
  const teamMatches = sameTeam ? (scope === 'total'
    ? recordedStatistic(team, 'partidos', 'partidosJugados')
    : recordedVenueTotal(team, 'partidos', 'partidosJugados', scope)) : null;
  const sameCompetition = competition?.id != null && player?.idCompeticion != null
    && String(competition.id) === String(player.idCompeticion)
    && competition.idTemporada != null && String(competition.idTemporada) === String(player.idTemporada);
  const duration = sameCompetition ? reportNumber(competition.minutosJuego) : null;
  const possibleMinutes = duration > 0 && teamMatches !== null ? duration * teamMatches : null;
  const compatibleCounts = played === null || called === null || played <= called;
  const compatibleTeam = compatibleCounts && (played === null || teamMatches === null || played <= teamMatches)
    && (called === null || teamMatches === null || called <= teamMatches);
  return {
    minutesPerAppearance: played > 0 && minutes !== null ? minutes / played : null,
    startCallupPercent: compatibleCounts && (starts === null || played === null || starts <= played)
      ? recordedShare(starts, called) : null,
    appearanceCallupPercent: recordedShare(played, called),
    callupTeamPercent: compatibleTeam ? recordedShare(called, teamMatches) : null,
    minutesTeamPercent: compatibleTeam ? recordedShare(minutes, possibleMinutes) : null,
    played, called, starts, minutes, teamMatches, duration, possibleMinutes,
  };
}

const CONTEXTS = ['GanandoLocal', 'EmpatandoLocal', 'PerdiendoLocal', 'GanandoVisitante', 'EmpatandoVisitante', 'PerdiendoVisitante'];
export const PLAYER_STATISTIC_FIELDS = {
  partidos: ['partidosJugados', 'convocatorias', 'titularidades', 'vecesCapitan']
    .flatMap(field => [`${field}Local`, `${field}Visitante`]),
  minutos: ['minutosJugadosLocal', 'minutosJugadosVisitante'],
  goles: ['golesTotales', 'golesTotalesPenalti', 'golesTotalesLocal', 'golesTotalesVisitante',
    'golesTotalesPenaltiLocal', 'golesTotalesPenaltiVisitante', 'golesTitularLocal', 'golesTitularVisitante',
    'golesSuplenteLocal', 'golesSuplenteVisitante'],
  tarjetas: ['tarjetasAmarillas', 'segundasAmarillas', 'tarjetasRojas']
    .flatMap(prefix => CONTEXTS.map(context => `${prefix}${context}`)),
};

export const canonicalPlayerStatistics = player => ({
  ...player,
  ...Object.fromEntries(Object.entries(PLAYER_STATISTIC_FIELDS).map(([section, fields]) => [
    section, Object.fromEntries(fields.map(field => [field, playerStatisticValue(player, section, field)])),
  ])),
});

export const aggregatePlayerStatistics = records => {
  const canonical = records.map(canonicalPlayerStatistics);
  return Object.fromEntries(Object.entries(PLAYER_STATISTIC_FIELDS).map(([section, fields]) => [
    section, Object.fromEntries(fields.map(field => [
      field, canonical.reduce((sum, player) => sum + player[section][field], 0),
    ])),
  ]));
};

export const playerGoalIntervals = player => ['0_15', '15_30', '30_45', '45_60', '60_75', '75_90'].map(interval => {
  const home = playerStatisticValue(player, 'goles', `golesLocal${interval}`) +
    playerStatisticValue(player, 'goles', `golesPenaltiLocal${interval}`);
  const away = playerStatisticValue(player, 'goles', `golesVisitante${interval}`) +
    playerStatisticValue(player, 'goles', `golesPenaltiVisitante${interval}`);
  return { label: `${interval.replace('_', '-')} min`, home, away, total: home + away };
});

export const playerGoalRounds = player => {
  const rounds = new Map();
  ['rachaGolesLocal', 'rachaGolesVisitante', 'rachaGolesPenaltiLocal', 'rachaGolesPenaltiVisitante'].forEach(field => {
    const values = player?.goles?.[field] ?? player?.[field];
    if (!Array.isArray(values)) return;
    values.forEach(value => {
      const round = Number(value);
      if (Number.isInteger(round) && round > 0) rounds.set(round, (rounds.get(round) || 0) + 1);
    });
  });
  return [...rounds].sort(([a], [b]) => a - b).map(([round, total]) => ({ round, total }));
};

export const playerCareerRows = (records = [], season) => {
  const groups = new Map();
  records.filter(record => !season || String(record.idTemporada) === String(season)).forEach(record => {
    const key = `${record.idTemporada}:${record.idCompeticion}:${record.idEquipo ?? record.equipo ?? ''}`;
    const group = groups.get(key) || { player: record, records: [] };
    group.records.push(record);
    groups.set(key, group);
  });
  return [...groups.values()].map(({ player, records: entries }) => ({
    ...player, ...aggregatePlayerStatistics(entries),
  })).sort((a, b) => Number(b.idTemporada) - Number(a.idTemporada));
};
